import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { getQuoteDetail, respondToQuote, getQuoteResponses } from '@/lib/api';
import { 
  ArrowLeft, 
  Package, 
  Calendar, 
  MapPin, 
  MessageSquare,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Loader2,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const QuoteDetail = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const { user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [response, setResponse] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [quoteData, responsesData] = await Promise.all([
          getQuoteDetail(quoteId),
          getQuoteResponses(quoteId).catch(() => [])
        ]);
        setQuote(quoteData);
        setResponses(responsesData || []);
      } catch (error) {
        console.error('Error fetching quote:', error);
        toast.error(isUSA ? 'Quote not found' : 'Cotización no encontrada');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [quoteId, isUSA]);

  const isRequester = user?.company_id === quote?.requester_company_id;
  const isProvider = quote?.target_company_ids?.includes(user?.company_id);

  const handleRespond = async (status) => {
    setResponding(true);
    try {
      await respondToQuote(quoteId, { status, response });
      toast.success(isUSA ? 'Response sent' : 'Respuesta enviada');
      setQuote({ ...quote, status: 'respondida' });
      // Reload responses
      const newResponses = await getQuoteResponses(quoteId);
      setResponses(newResponses || []);
      setResponse('');
    } catch (error) {
      toast.error(isUSA ? 'Error sending response' : 'Error al enviar respuesta');
    } finally {
      setResponding(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      nueva: { label: isUSA ? 'New' : 'Nueva', class: 'bg-blue-100 text-blue-700' },
      pending: { label: isUSA ? 'Pending' : 'Pendiente', class: 'bg-yellow-100 text-yellow-700' },
      respondida: { label: isUSA ? 'Responded' : 'Respondida', class: 'bg-purple-100 text-purple-700' },
      en_revision: { label: isUSA ? 'In Review' : 'En Revisión', class: 'bg-orange-100 text-orange-700' },
      negociacion: { label: isUSA ? 'Negotiating' : 'Negociación', class: 'bg-amber-100 text-amber-700' },
      accepted: { label: isUSA ? 'Accepted' : 'Aceptada', class: 'bg-green-100 text-green-700' },
      cerrada: { label: isUSA ? 'Closed' : 'Cerrada', class: 'bg-emerald-100 text-emerald-700' },
      rejected: { label: isUSA ? 'Rejected' : 'Rechazada', class: 'bg-red-100 text-red-700' },
      cancelada: { label: isUSA ? 'Cancelled' : 'Cancelada', class: 'bg-red-100 text-red-700' },
      completed: { label: isUSA ? 'Completed' : 'Completada', class: 'bg-emerald-100 text-emerald-700' }
    };
    const config = statusConfig[status] || statusConfig.nueva;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">
          {isUSA ? 'Quote not found' : 'Cotización no encontrada'}
        </h2>
        <Button variant="outline" onClick={() => navigate('/dashboard/quotes')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isUSA ? 'Back to quotes' : 'Volver a cotizaciones'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quote-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/quotes')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isUSA ? 'Quote Request' : 'Solicitud de Cotización'}
            </h1>
            <p className="text-sm text-slate-500">{quote.quote_id}</p>
          </div>
        </div>
        {getStatusBadge(quote.status)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                {isUSA ? 'Product Details' : 'Detalles del Producto'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Product Type' : 'Tipo de Producto'}</p>
                  <p className="font-medium">{quote.product_type || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Quantity' : 'Cantidad'}</p>
                  <p className="font-medium">{quote.quantity?.toLocaleString() || '-'} {quote.unit || 'unidades'}</p>
                </div>
                {quote.desired_composition && (
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Composition' : 'Composición'}</p>
                    <p className="font-medium">{quote.desired_composition}</p>
                  </div>
                )}
                {quote.desired_weight && (
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Weight' : 'Peso'}</p>
                    <p className="font-medium">{quote.desired_weight}</p>
                  </div>
                )}
                {quote.color && (
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Color' : 'Color'}</p>
                    <p className="font-medium">{quote.color}</p>
                  </div>
                )}
              </div>

              {quote.specifications && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">{isUSA ? 'Specifications' : 'Especificaciones'}</p>
                  <p className="bg-slate-50 p-3 rounded-lg">{quote.specifications}</p>
                </div>
              )}

              {quote.comments && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">{isUSA ? 'Additional Comments' : 'Comentarios Adicionales'}</p>
                  <p className="bg-slate-50 p-3 rounded-lg">{quote.comments}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Response Section - Only show for providers on new quotes */}
          {isProvider && (quote.status === 'nueva' || quote.status === 'pending') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                  {isUSA ? 'Respond to Quote' : 'Responder Cotización'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder={isUSA ? 'Write your response (price, availability, conditions...)' : 'Escribe tu respuesta (precio, disponibilidad, condiciones...)'}
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  rows={4}
                />
                <div className="flex gap-3">
                  <Button 
                    onClick={() => handleRespond('accepted')}
                    disabled={responding}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {responding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    {isUSA ? 'Accept & Send Quote' : 'Aceptar y Enviar Cotización'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => handleRespond('rejected')}
                    disabled={responding}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {isUSA ? 'Decline' : 'Rechazar'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Responses Section - Show to requester and providers */}
          {responses.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-emerald-600" />
                  {isUSA ? 'Responses' : 'Respuestas'} ({responses.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {responses.map((resp, idx) => (
                  <div key={resp.response_id || idx} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-sm">{resp.responder_company_name || 'Proveedor'}</span>
                      </div>
                      <Badge className={resp.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {resp.status === 'accepted' 
                          ? (isUSA ? 'Accepted' : 'Aceptado') 
                          : (isUSA ? 'Declined' : 'Rechazado')}
                      </Badge>
                    </div>
                    {resp.response && (
                      <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{resp.response}</p>
                    )}
                    {(resp.unit_price || resp.total_price) && (
                      <div className="flex gap-4 text-sm">
                        {resp.unit_price && <span>Precio unitario: <strong>${resp.unit_price}</strong></span>}
                        {resp.total_price && <span>Total: <strong>${resp.total_price}</strong></span>}
                      </div>
                    )}
                    {resp.delivery_time && (
                      <p className="text-sm text-slate-500">Tiempo de entrega: {resp.delivery_time}</p>
                    )}
                    <p className="text-xs text-slate-400">
                      {new Date(resp.created_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX', {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* No responses message for requester */}
          {isRequester && responses.length === 0 && quote.status === 'nueva' && (
            <Card>
              <CardContent className="py-8 text-center">
                <MessageCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">{isUSA ? 'Waiting for responses...' : 'Esperando respuestas...'}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{isUSA ? 'Request Info' : 'Info de Solicitud'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'From' : 'De'}</p>
                  <p className="font-medium">{quote.requester_company_name || quote.requester_company_id}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">{isUSA ? 'Received' : 'Recibida'}</p>
                  <p className="font-medium">
                    {quote.created_at ? new Date(quote.created_at).toLocaleDateString('es-MX', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : '-'}
                  </p>
                </div>
              </div>

              {quote.required_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Required Date' : 'Fecha Requerida'}</p>
                    <p className="font-medium">{quote.required_date}</p>
                  </div>
                </div>
              )}

              {quote.delivery_city && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">{isUSA ? 'Delivery Location' : 'Lugar de Entrega'}</p>
                    <p className="font-medium">{quote.delivery_city}</p>
                  </div>
                </div>
              )}

              {quote.attachment_url && (
                <div>
                  <a 
                    href={quote.attachment_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:underline text-sm flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    {isUSA ? 'View Attachment' : 'Ver Adjunto'}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetail;
